import React, { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch, get } from "react-hook-form";
import { InputGroup } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSearch from "components/atoms/ExpandableSearch/ExpandableSearch";
import Button from "components/atoms/Button/Button";

type DownloadTemplateFilterProps = {
  options: Array<any>;
  name: string;
};

function DownloadTemplateFilter(props: DownloadTemplateFilterProps) {
  const intl = useIntl();
  const { options, name } = props;
  const {
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const [searchText, setSearchText] = useState("");
  const selectedOptions = useWatch({ name, defaultValue: [] });

  const filteredOptions = useMemo(() => {
    if (searchText?.trim()?.length) {
      return options.filter((item: any) => item?.label?.toLowerCase().includes(searchText?.toLowerCase()));
    }
    return options;
  }, [searchText, options]);

  // clear values on unmount
  useEffect(
    () => () => {
      setValue(name, []);
      clearErrors(name);
    },
    [],
  );

  const handleOptionChecked = (value: string) => {
    if (selectedOptions.includes(value)) {
      setValue(
        name,
        selectedOptions.filter((item: any) => item !== value),
      );
    } else {
      setValue(name, [...selectedOptions, value]);
    }
    clearErrors(name);
  };

  const handleSelectAll = () => {
    setValue(
      name,
      options.map((item: any) => item.value),
    );
    clearErrors(name);
  };

  const handleClearAll = () => {
    setValue(name, []);
  };

  return (
    <div className="d-flex flex-column align-items-start">
      <ExpandableSearch
        containerClasses="mb-3"
        value={searchText}
        alwaysExpanded
        onChange={(e) => setSearchText(e.target.value)}
      />
      <div className="dt-filter-option-container">
        {filteredOptions.map((item) => (
          <div className="mb-3 mr-10" key={item.value}>
            <InputGroup className="checkbox bg-transparent">
              <label htmlFor={item.label} className="d-flex align-items-center cursor-pointer mb-0 w-100">
                <input
                  id={item.label}
                  type="checkbox"
                  className="d-none"
                  onChange={() => handleOptionChecked(item.value)}
                  checked={selectedOptions.includes(item.value)}
                />
                <span className="border-dark border-solid border-1 mr-2 position-relative br-3 checkbox__box" />
                <span className="text-primary mr-auto">{item.label}</span>
              </label>
            </InputGroup>
          </div>
        ))}
        {!!searchText?.length && !filteredOptions.length && (
          <p className="font-b3">No results found matching {searchText}</p>
        )}
      </div>
      {get(errors, name) && (
        <div className="d-block font-b3 invalid-feedback">
          {get(errors, name)?.message ? (
            get(errors, name).message
          ) : (
            <FormattedMessage id="general.requiredField" defaultMessage="This is a required field" />
          )}
        </div>
      )}
      <div className="d-flex align-items-center mt-2">
        <Button
          variant="primary"
          className="rounded-pill blue-200 mr-2 h-26"
          label={intl.formatMessage({
            id: "general.selectAll",
            defaultMessage: "Select all",
          })}
          onClick={handleSelectAll}
        />
        <Button
          variant="outline-primary"
          label={intl.formatMessage({
            id: "general.clearAll",
            defaultMessage: "Clear all",
          })}
          onClick={handleClearAll}
        />
      </div>
    </div>
  );
}

export default DownloadTemplateFilter;
